import { Permission, PrivilegeType } from 'src/services/role';

import { DepartmentItem } from 'src/types/category';

import { getAllSubDepartments } from './categoryTransform';

export type DepartmentPrivilegeBase =
  | PrivilegeType.PLAN
  | PrivilegeType.PROJECT
  | PrivilegeType.INCOME_ANALYSIS
  | PrivilegeType.EXPENSE_ANALYSIS
  | PrivilegeType.VARIANCE_ANALYSIS
  | PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION
  | PrivilegeType.PROPOSAL
  | PrivilegeType.ACTION_PLAN;

const GLOBAL_PRIVILEGE_MAP: Record<DepartmentPrivilegeBase, PrivilegeType> = {
  [PrivilegeType.PLAN]: PrivilegeType.PLAN_GLOBAL,
  [PrivilegeType.PROJECT]: PrivilegeType.PROJECT_GLOBAL,
  [PrivilegeType.INCOME_ANALYSIS]: PrivilegeType.INCOME_ANALYSIS_GLOBAL,
  [PrivilegeType.EXPENSE_ANALYSIS]: PrivilegeType.EXPENSE_ANALYSIS_GLOBAL,
  [PrivilegeType.VARIANCE_ANALYSIS]: PrivilegeType.VARIANCE_ANALYSIS_GLOBAL,
  [PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION]:
    PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION_GLOBAL,
  [PrivilegeType.PROPOSAL]: PrivilegeType.PROPOSAL_GLOBAL,
  [PrivilegeType.ACTION_PLAN]: PrivilegeType.ACTION_PLAN_GLOBAL,
};

const DIRECT_DEPT_PRIVILEGE_MAP: Record<DepartmentPrivilegeBase, PrivilegeType> = {
  [PrivilegeType.PLAN]: PrivilegeType.PLAN_DIRECT_DEPT,
  [PrivilegeType.PROJECT]: PrivilegeType.PROJECT_DIRECT_DEPT,
  [PrivilegeType.INCOME_ANALYSIS]: PrivilegeType.INCOME_ANALYSIS_DIRECT_DEPT,
  [PrivilegeType.EXPENSE_ANALYSIS]: PrivilegeType.EXPENSE_ANALYSIS_DIRECT_DEPT,
  [PrivilegeType.VARIANCE_ANALYSIS]: PrivilegeType.VARIANCE_ANALYSIS_DIRECT_DEPT,
  [PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION]:
    PrivilegeType.OPERATIONAL_STATEMENT_TRANSACTION_DIRECT_DEPT,
  [PrivilegeType.PROPOSAL]: PrivilegeType.PROPOSAL_DIRECT_DEPT,
  [PrivilegeType.ACTION_PLAN]: PrivilegeType.ACTION_PLAN_DIRECT_DEPT,
};

/**
 * 按模块部门读取权限过滤部门列表：
 * - GLOBAL：全部部门
 * - DIRECT_DEPT：上一级父部门 + 它的所有子孙部门
 * - READ：自己部门 + 所有子孙部门
 */
export function filterDepartmentsByPrivilege(
  departments: DepartmentItem[],
  userDepartmentId: string | number | undefined | null,
  permissions: Permission | undefined,
  privilegeBase: DepartmentPrivilegeBase
): DepartmentItem[] {
  if (!departments.length) {
    return [];
  }

  const hasGlobal = !!permissions?.[GLOBAL_PRIVILEGE_MAP[privilegeBase]]?.read;

  if (hasGlobal) {
    return departments;
  }

  const hasDirectDept = !!permissions?.[DIRECT_DEPT_PRIVILEGE_MAP[privilegeBase]]?.read;
  const hasDeptRead = !!permissions?.[privilegeBase]?.read;

  if (!hasDirectDept && !hasDeptRead) {
    return [];
  }

  if (userDepartmentId == null || userDepartmentId === '') {
    return [];
  }

  const ownId = String(userDepartmentId);
  const ownDept = departments.find((dept) => String(dept.id) === ownId);
  if (!ownDept) {
    return [];
  }

  const parent =
    hasDirectDept && ownDept.parentId
      ? departments.find((dept) => String(dept.id) === String(ownDept.parentId))
      : undefined;
  const root = parent ?? ownDept;
  const allowedIds = new Set<string>([
    String(root.id),
    ...getAllSubDepartments(departments, root.id).map((dept) => String(dept.id)),
  ]);

  return departments.filter((dept) => allowedIds.has(String(dept.id)));
}
